import React, { useState, useEffect } from 'react';
import { useAuth } from '../utils/AuthContext';
import { useLanguage } from '../utils/i18n';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, CreditCard, Loader2 } from 'lucide-react';
import { supabase } from '../utils/supabase';

export default function PastDue() {
    const { session, business, subscriptionStatus, loading: authLoading, signOut } = useAuth();
    const { t } = useLanguage();
    const navigate = useNavigate();
    const [loadingPortal, setLoadingPortal] = useState(false);

    useEffect(() => {
        if (!authLoading && !session) {
            navigate('/login', { replace: true });
        }
    }, [authLoading, session, navigate]);

    useEffect(() => {
        if (!authLoading && subscriptionStatus === 'active') {
            navigate('/dashboard', { replace: true });
        }
    }, [authLoading, subscriptionStatus, navigate]);

    const handleUpdatePayment = async () => {
        if (!business?.id || !session) return;
        setLoadingPortal(true);

        try {
            const { data: { session: currentSession } } = await supabase.auth.getSession();
            const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}/api/polar/customer-portal`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${currentSession.access_token}`
                },
                body: JSON.stringify({ businessId: business.id })
            });

            const data = await res.json();
            if (data.url) {
                if (data.url.startsWith('/')) {
                    window.location.href = data.url;
                } else {
                    window.location.href = data.url;
                }
            } else {
                throw new Error(data.error || 'Failed to get portal URL');
            }
        } catch (err) {
            console.error('[PastDue] Error opening portal:', err);
            alert(t('past_due_portal_err'));
        } finally {
            setLoadingPortal(false);
        }
    };

    if (authLoading || !business) {
        return (
            <div style={{ display: 'flex', height: '100vh', justifyContent: 'center', alignItems: 'center', background: 'var(--bg)' }}>
                <Loader2 size={48} className="spin" color="var(--primary)" />
            </div>
        );
    }

    return (
        <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
            <div className="glass-card" style={{ maxWidth: 460, width: '100%', textAlign: 'center', padding: '48px 32px' }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
                    <div style={{ background: 'rgba(239, 68, 68, 0.1)', padding: 16, borderRadius: '50%', color: 'var(--danger)' }}>
                        <AlertCircle size={48} />
                    </div>
                </div>

                <h1 style={{ fontSize: 28, fontWeight: 700, color: 'var(--text)', marginBottom: 16 }}>
                    {t('past_due_title')}
                </h1>

                <p style={{ color: 'var(--text-muted)', fontSize: 16, lineHeight: 1.6, marginBottom: 32 }}>
                    {t('past_due_desc')}
                </p>

                <button
                    onClick={handleUpdatePayment}
                    disabled={loadingPortal}
                    className="btn btn-glow"
                    style={{
                        width: '100%',
                        background: 'var(--primary)',
                        color: 'white',
                        display: 'flex',
                        justifyContent: 'center',
                        gap: 10,
                        padding: 16,
                        opacity: loadingPortal ? 0.7 : 1
                    }}
                >
                    {loadingPortal ? (
                        <Loader2 size={20} className="spin" />
                    ) : (
                        <>
                            <CreditCard size={20} />
                            {t('past_due_btn')}
                        </>
                    )}
                </button>

                <button
                    onClick={signOut}
                    className="btn"
                    style={{
                        background: 'transparent',
                        color: 'var(--text-muted)',
                        marginTop: 16,
                        width: '100%',
                        justifyContent: 'center',
                        fontSize: 14,
                        border: '1px solid transparent'
                    }}
                >
                    {t('sign_out')}
                </button>
            </div>
        </div>
    );
}
